import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { format, addDays, subDays, addWeeks, addMonths, startOfWeek, startOfMonth, endOfMonth, isSameDay, isBefore, isAfter, startOfDay } from "date-fns";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";


import { useWindowDimensions } from "../../../../utils/util";
import { getAvailByTeacher } from "../../../../store/actions/availability";
import { reSchedule } from "../../../../store/actions/student";
import { getAllSessions } from "../../../../store/actions/session";
import modalStyles from "./popupModal/styles.module.css";
import "./calendar.css";

import styles from "../../teacher/availability/gridStyle.module.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIME_SLOTS = [];
for (let hour = 0; hour < 24; hour++) {
  for (let min of ["00", "30"]) {
    const h12 = hour % 12 || 12;
    const ampm = hour < 12 ? "AM" : "PM";
    TIME_SLOTS.push({
      id: `${hour.toString().padStart(2, "0")}:${min}`,
      label: `${h12}:${min} ${ampm}`
    });
  }
}

function MyCalendar({ teacherData: propTeacherData, course, coupon }) {
  useWindowDimensions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isTrail = location.state?.trail || false;

  const [viewMode, setViewMode] = useState("today"); // "today", "week", "month"
  const [baseDate, setBaseDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [popupModal, setPopupModal] = useState(null);

  const now = useMemo(() => new Date(), []);

  const rescheduleObj = useMemo(() => {
    try {
      const stored = localStorage.getItem("rescheduleObj");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const getTeacherAvail = useCallback(async () => {
    if (!propTeacherData?._id) return;

    const sessionsRes = await dispatch(getAllSessions());
    let bookedSlotIds = [];
    if (sessionsRes?.success && Array.isArray(sessionsRes.sessions)) {
      bookedSlotIds = sessionsRes.sessions
        .filter(s => s && s.status !== "Cancelled")
        .reduce((acc, s) => {
          if (s.availabilityId) acc.push(s.availabilityId);
          if (s.availabilityIds) acc.push(...s.availabilityIds);
          return acc;
        }, []);
    }

    const result = await getAvailByTeacher(propTeacherData._id);
    if (result.success && Array.isArray(result.data)) {
      const slots = result.data
        .map((slot) => ({
          id: slot._id,
          start: new Date(slot.from),
          end: new Date(slot.to),
          isBooked: bookedSlotIds.includes(slot._id) || slot.isBooked || (slot.bookedCount >= 1),
          isFull: (10 - (slot.bookedCount || 0)) <= 0,
          capacityLeft: 10 - (slot.bookedCount || 0)
        }))
        .filter(slot => isAfter(slot.start, now)); // Only future slots
      setAvailability(slots);
    }
  }, [propTeacherData, dispatch, now]);

  useEffect(() => {
    getTeacherAvail();
  }, [getTeacherAvail]);

  // Get dates for the current view
  const calendarDates = useMemo(() => {
    if (viewMode === "today") {
      return [startOfDay(new Date())];
    } else if (viewMode === "week") {
      const start = startOfWeek(baseDate, { weekStartsOn: 0 });
      return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
    } else {
      // Month view
      const monthStart = startOfMonth(baseDate);
      const monthEnd = endOfMonth(baseDate);
      const start = startOfWeek(monthStart, { weekStartsOn: 0 });
      const dates = [];
      let current = start;
      while (isBefore(current, monthEnd) || isSameDay(current, monthEnd) || dates.length % 7 !== 0) {
        dates.push(current);
        current = addDays(current, 1);
        if (dates.length > 42) break; // Max 6 weeks
      }
      return dates;
    }
  }, [viewMode, baseDate]);

  // Get availability count for a specific date
  const getAvailabilityForDate = useCallback((date) => {
    return availability.filter(slot => isSameDay(slot.start, date) && !slot.isBooked && !slot.isFull);
  }, [availability]);

  // Get all potential time slots for the selected date to show all states
  const selectedDateSlots = useMemo(() => {
    const fullSlots = [];
    const coveredTimes = new Set();

    TIME_SLOTS.forEach(ts => {
      const [h, m] = ts.id.split(":").map(Number);
      const cellStart = new Date(selectedDate);
      cellStart.setHours(h, m, 0, 0);

      if (coveredTimes.has(cellStart.getTime())) return;

      const slot = availability.find(s => Math.abs(s.start - cellStart) < 1000);
      if (slot) {
        const durationMins = moment(slot.end).diff(moment(slot.start), 'minutes');
        const numBlocks = Math.ceil(durationMins / 30);
        for (let i = 1; i < numBlocks; i++) {
          coveredTimes.add(moment(cellStart).add(i * 30, 'minutes').toDate().getTime());
        }
      }
      fullSlots.push({ timeSlot: ts, cellStart, slot });
    });
    return fullSlots;
  }, [availability, selectedDate]);

  const handleDayClick = (date) => {
    if (isBefore(startOfDay(date), startOfDay(now))) return; // Don't allow past dates
    setSelectedDate(date);
  };

  const handleBookClick = (slot) => {
    if (!slot || slot.isBooked || slot.isFull) {
      return toast.warning("This slot is not available");
    }

    setPopupModal({
      slots: [slot],
      start: slot.start,
      end: slot.end
    });
  };

  const confirmBooking = async (event) => {
    const token = JSON.parse(localStorage.getItem("profile"))?.token;
    if (!token) return toast.warn("Please Login to continue");

    const availabilityIds = event.slots.map(s => s.id);
    const bookingEvent = {
      id: availabilityIds[0],
      availabilityIds: availabilityIds,
      start: event.start,
      end: event.end
    };

    localStorage.setItem("chosenEvent", JSON.stringify(bookingEvent));

    if (rescheduleObj) {
      handleReschedule(bookingEvent);
    } else {
      navigate("/payment", {
        state: { course, teacherData: propTeacherData, event: bookingEvent, trail: isTrail, coupon },
      });
    }
  };

  const handleReschedule = async (eventData) => {
    const studentData = JSON.parse(localStorage.getItem("studentData") || "{}");
    const courseLocal = JSON.parse(localStorage.getItem("chosenCourse") || "{}");

    let body = {
      courseId: courseLocal._id,
      studentId: studentData?.data?.userId,
      teacherId: courseLocal.userId.onType._id,
      type: "1",
      from: eventData.start,
      to: eventData.end,
      availabilityIds: eventData.availabilityIds,
      session: rescheduleObj
    };

    try {
      const result = await dispatch(reSchedule(body));
      if (result.success) {
        toast.success("Rescheduled successfully");
        localStorage.removeItem("rescheduleObj");
        navigate("/student/dashboard");
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error("Failed to reschedule");
    }
  };

  const navigateCalendar = (direction) => {
    if (viewMode === "today") {
      const newDate = direction > 0 ? addDays(baseDate, 1) : subDays(baseDate, 1);
      setBaseDate(newDate);
      setSelectedDate(newDate);
    } else if (viewMode === "week") {
      setBaseDate(prev => direction > 0 ? addWeeks(prev, 1) : addWeeks(prev, -1));
    } else if (viewMode === "month") {
      setBaseDate(prev => direction > 0 ? addMonths(prev, 1) : addMonths(prev, -1));
    }
  };

  const isSlotPast = (slot) => {
    if (!slot) return false;
    return isBefore(slot.start || slot, now);
  };

  const PopupModal = ({ event }) => (
    <div className={modalStyles.modalBackdrop}>
      <div className={modalStyles.modal} style={{ maxWidth: "450px" }}>
        <i className={`${modalStyles.closeBtn} fas fa-close`} onClick={() => setPopupModal(null)}></i>
        <h3 className={modalStyles.modalHeading}>{isTrail ? "Confirm Free Trial" : "Confirm Booking"}</h3>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ marginBottom: "20px" }}>
            <div style={{ color: "#888", fontSize: "0.9rem" }}>Teacher</div>
            <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>{propTeacherData?.firstName?.data || propTeacherData?.firstName} {propTeacherData?.lastName?.data || propTeacherData?.lastName}</div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: "30px" }}>
            <div>
              <div style={{ color: "#888", fontSize: "0.9rem" }}>Date</div>
              <div style={{ fontWeight: "700" }}>{format(event.start, "MMM dd, yyyy")}</div>
            </div>
            <div>
              <div style={{ color: "#888", fontSize: "0.9rem" }}>Time</div>
              <div style={{ fontWeight: "700" }}>{format(event.start, "hh:mm a")} - {format(event.end, "hh:mm a")}</div>
            </div>
          </div>
          <div className="slotBtn">
            <button style={{ width: "100%", borderRadius: "10px", padding: "12px", cursor: "pointer" }} onClick={() => confirmBooking(event)}>Confirm & Proceed to Payment</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.matrixContainer}>
      <div style={{ marginBottom: "15px", fontWeight: "800", fontSize: "1.1rem", color: "#dc3545" }}>AVAILABILITY CALENDAR</div>
      <div className={styles.toolbar}>
        <div className={styles.dateDisplay}>
          {viewMode === "today" && format(baseDate, "EEEE, MMMM dd, yyyy")}
          {viewMode === "week" && `Week of ${format(startOfWeek(baseDate), "MMM dd")} - ${format(addDays(startOfWeek(baseDate), 6), "MMM dd, yyyy")}`}
          {viewMode === "month" && format(baseDate, "MMMM yyyy")}
        </div>
        <div className={styles.buttonGroup}>
          <button className={`${styles.navButton} ${viewMode === "today" ? styles.active : ""}`} onClick={() => { setViewMode("today"); setBaseDate(new Date()); setSelectedDate(new Date()); }}>Today</button>
          <button className={`${styles.navButton} ${viewMode === "week" ? styles.active : ""}`} onClick={() => { setViewMode("week"); setBaseDate(new Date()); }}>Week</button>
          <button className={`${styles.navButton} ${viewMode === "month" ? styles.active : ""}`} onClick={() => { setViewMode("month"); setBaseDate(new Date()); }}>Month</button>

          <div style={{ display: "flex", gap: "5px", marginLeft: "5px" }}>
            <button className={styles.navButton} onClick={() => navigateCalendar(-1)}><i className="fas fa-chevron-left"></i></button>
            <button className={styles.navButton} onClick={() => navigateCalendar(1)}><i className="fas fa-chevron-right"></i></button>
          </div>
        </div>
      </div>

      {(viewMode === "week" || viewMode === "month") && (
        <div className={styles.calendarGrid}>
          {WEEKDAYS.map(day => <div key={day} className={styles.dayHeader}>{day}</div>)}
          {calendarDates.map((date, idx) => {
            const dayAvail = getAvailabilityForDate(date);
            const isSelected = isSameDay(date, selectedDate);
            const isToday = isSameDay(date, new Date());
            const isPast = isBefore(startOfDay(date), startOfDay(new Date()));
            const isCurrentMonth = viewMode === "month" ? date.getMonth() === baseDate.getMonth() : true;

            return (
              <div
                key={idx}
                className={`${styles.dayCell} ${isSelected ? styles.isSelected : ""} ${isToday ? styles.isToday : ""} ${dayAvail.length > 0 ? styles.hasAvailability : ""} ${isPast || !isCurrentMonth ? styles.disabled : ""}`}
                style={{ opacity: isCurrentMonth ? (isPast ? 0.3 : 1) : 0.2 }}
                onClick={() => !isPast && isCurrentMonth && handleDayClick(date)}
              >
                <span className={styles.dayNumber}>{format(date, "d")}</span>
                {dayAvail.length > 0 && <div className={styles.availabilityDot} />}
              </div>
            );
          })}
        </div>
      )}

      <div>
        {window.location.pathname !== "/teacher-profile" && (
          <div className={styles.selectedDateHeader}>
            <span>{format(selectedDate, "EEEE, MMMM dd, yyyy")}</span>
            <span>{selectedDateSlots.filter(s => s.slot && !s.slot.isBooked && !s.slot.isFull && !isSlotPast(s.cellStart)).length} slots available</span>
          </div>
        )}
        <div className={styles.timeSlotGrid}>
          {selectedDateSlots.map(({ timeSlot, cellStart, slot }) => {
            const isPast = isSlotPast(cellStart);
            const isBooked = slot?.isBooked || slot?.isFull;
            const exists = !!slot;

            return (
              <button
                key={timeSlot.id}
                className={`${styles.timeSlotButton} ${isBooked ? styles.isBooked : (exists ? styles.exists : styles.notAvailable)} ${isPast ? styles.isPast : ""}`}
                disabled={!exists || isBooked || isPast}
                onClick={() => exists && !isPast && handleBookClick(slot)}
              >
                {exists ? `${format(slot.start, "hh:mm a")} - ${format(slot.end, "hh:mm a")}` : timeSlot.label}
                {isBooked ? (slot.isBooked ? " (Booked)" : " (Full)") : (exists ? " (Available)" : " (N/A)")}
              </button>
            );
          })}
        </div>
      </div>

      {popupModal && <PopupModal event={popupModal} />}
    </div>
  );
}

MyCalendar.propTypes = {
  teacherData: PropTypes.object,
  course: PropTypes.object,
  coupon: PropTypes.object
};

export default MyCalendar;
